import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Activity, RefreshCw } from 'lucide-react';
import { platformDiagnostics, type PlatformDiagnostics } from '../../lib/health-api';
import { fmtDuration, fmtTime, HealthBadge, IssueList, SummaryChips } from './HealthParts';
import { Button } from '../../components/ui';
import styles from './admin.module.css';

// Platform diagnostics (super-admin): database, job queues, leader locks,
// configuration, and every integration that needs attention across all sites.
export function AdminHealth() {
  const [data, setData] = useState<PlatformDiagnostics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await platformDiagnostics());
      setError(null);
    } catch {
      setError('Could not load diagnostics.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 30_000);
    return () => clearInterval(t);
  }, [load]);

  const failing = data?.checks.filter((c) => !c.ok) ?? [];

  return (
    <div>
      <div className={styles.headerRow}>
        <div className={styles.headerTitles}>
          <div className={styles.kicker}>
            <Activity size={13} />
            <span>Platform Engine</span>
          </div>
          <h1 className={styles.title}>System Health &amp; Diagnostics</h1>
          <p className={styles.subtitle}>
            Real-time platform diagnostics, subsystem integrity checks, and worker heartbeat monitors.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {data && <span className={styles.hint} style={{ fontSize: 12 }}>Updated {fmtTime(data.generatedAt)}</span>}
          <Button
            variant="outline"
            size="sm"
            disabled={loading}
            onClick={() => void load()}
            className="gap-2 h-9 px-3"
          >
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
            <span>Refresh</span>
          </Button>
        </div>
      </div>
      {error && <p className={styles.error} style={{ marginBottom: 16 }} role="alert">{error}</p>}

      {data && (
        <>
          <section className={styles.section}>
            <h3 className={styles.label}>System checks</h3>
            <p className={styles.hint} style={{ marginTop: 0 }}>
              {failing.length === 0 ? 'All checks pass.' : `${failing.length} check${failing.length === 1 ? '' : 's'} failing.`}
            </p>
            <ul className={styles.issueList} style={{ listStyle: 'none', paddingLeft: 0 }}>
              {data.checks.map((c) => (
                <li key={c.name}>
                  <span className={c.ok ? styles.stepOk : styles.stepFail}>{c.ok ? '✓' : c.severity === 'critical' ? '✗' : '!'}</span> <strong>{c.name}</strong> — {c.detail}
                </li>
              ))}
            </ul>
          </section>

          <section className={styles.section}>
            <h3 className={styles.label}>Job queues</h3>
            {data.queues.length === 0 ? (
              <p className={styles.hint}>No active jobs.</p>
            ) : (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead><tr><th>Queue</th><th>Pending</th><th>Running</th><th>Dead</th><th>Oldest due</th></tr></thead>
                  <tbody>
                    {data.queues.map((q) => (
                      <tr key={q.queue}>
                        <td className={styles.mono}>{q.queue}</td>
                        <td>{q.pending}</td>
                        <td>{q.running}</td>
                        <td className={q.dead > 0 ? styles.stepFail : undefined}>{q.dead}</td>
                        <td>{q.oldestDueMs === null ? '—' : fmtDuration(Math.round(q.oldestDueMs / 1000))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className={styles.hint}><Link to="/admin/jobs">Open the job queue →</Link></p>
          </section>

          <section className={styles.section}>
            <h3 className={styles.label}>Integrations</h3>
            <SummaryChips summary={data.connections.summary} />
            {data.connections.attention.length === 0 ? (
              <p className={styles.hint}>No integration needs attention.</p>
            ) : (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead><tr><th>Site</th><th>Connection</th><th>Health</th></tr></thead>
                  <tbody>
                    {data.connections.attention.map((c) => (
                      <tr key={c.connectionId}>
                        <td><Link to={`/admin/sites/${c.siteId}?tab=channels`}>{c.siteName ?? c.siteId}</Link></td>
                        <td>{c.name} <span className={styles.badgeMuted}>{c.channel}</span></td>
                        <td><HealthBadge state={c.health.state} /><IssueList health={c.health} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className={styles.section}>
            <h3 className={styles.label}>This server</h3>
            <p className={styles.hint} style={{ marginTop: 0 }}>
              v{data.version}{data.commit ? ` (${data.commit.slice(0, 7)})` : ''} · Node {data.process.node} · up {fmtDuration(data.process.uptimeSec)} · {data.process.rssMb} MB
            </p>
            <p className={styles.hint}>
              Singleton jobs held by this process:{' '}
              {data.leaders.filter((l) => l.heldHere).map((l) => l.key).join(', ') || 'none (another instance leads)'}
            </p>
          </section>
        </>
      )}
    </div>
  );
}
